/**
 * Ficha ou tabela de um plugin web (docs/spec/web-plugins.md, "O iframe"): a página do plugin num
 * iframe isolado (`sandbox="allow-scripts"`, origem nula), servida do endereço do pacote, e o
 * `WebHost` falando com ela. Diálogos e menus pedidos pela página são desenhados aqui, fora do
 * iframe, para não ficarem cortados na borda.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../../i18n/index.js';
import type { LibraryItem, RoomPlugin } from '../../shared.js';
import { serverUrl } from '../../serverUrl.js';
import { ContextMenu, type MenuEntry } from '../../ui/ContextMenu.js';
import { ImagePickerDialog, rpgDeskFileId } from '../../ui/ImagePicker.js';
import { askConfirm, askText } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { devUrlOf, useDevUrls } from './devMode.js';
import type { MenuItem } from './sdk.js';
import { WebHost, type HostState, type HostUi, type PageError } from './webHost.js';

/** Caminho do pacote, com cada parte codificada (espaços, acentos). */
const encodePath = (path: string) => path.split('/').map(encodeURIComponent).join('/');

export function WebPluginFrame({ plugin, item }: { plugin: RoomPlugin; item: LibraryItem }) {
  const t = useT();
  const ref = useRef<HTMLIFrameElement>(null);
  const hostRef = useRef<WebHost | null>(null);
  const [state, setState] = useState<HostState>('loading');
  const [errors, setErrors] = useState<PageError[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [reload, setReload] = useState(0);
  const [closed, setClosed] = useState(false);
  const [menu, setMenu] = useState<{ items: MenuItem[]; at: { x: number; y: number }; resolve: (id: string | null) => void } | null>(null);
  const [picker, setPicker] = useState<{ resolve: (id: string | null) => void } | null>(null);
  const devUrls = useDevUrls((s) => s.urls);
  const kind = item.kind === 'dock' ? 'table' : 'sheet';
  const entry = plugin.dataTypes.find((d) => d.id === item.dataType)?.entry ?? null;
  const dev = devUrlOf(devUrls, plugin.moduleId, item.dataType);
  const src = dev ?? (plugin.web && entry ? `${serverUrl(plugin.web.baseUrl)}${encodePath(entry)}` : null);

  // a página pede, o RPG Desk desenha (o iframe muda de vida, a interface não)
  const ui = useMemo<HostUi>(
    () => ({
      confirm: ({ title, message }) => askConfirm({ title, message }),
      prompt: ({ title, label, initial }) => askText({ title, label, initial, allowEmpty: true, maxLength: 1000 }),
      menu: (items, at) => new Promise((resolve) => setMenu({ items, at, resolve })),
      pickImage: () => new Promise((resolve) => setPicker({ resolve })),
      toast: (text, kind) => toast(`${plugin.name}: ${text}`, kind),
      openLink: async (url) => {
        const sure = await askConfirm({ title: t('webPlugin.link.title'), message: t('webPlugin.link.message', { plugin: plugin.name, url }), action: t('webPlugin.link.go') });
        if (sure) window.open(url, '_blank', 'noopener,noreferrer');
        return sure;
      },
    }),
    [plugin.name, t],
  );

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe || !src || closed) return;
    setState('loading');
    setErrors([]);
    const host = new WebHost({
      iframe,
      plugin,
      item,
      kind,
      ui,
      onState: setState,
      onError: (e) => setErrors((list) => [...list.slice(-49), e]),
    });
    hostRef.current = host;
    host.start();
    // o endereço vai depois dos ouvintes: o primeiro `hello` não se perde
    iframe.src = src;
    return () => {
      host.dispose();
      hostRef.current = null;
    };
    // o plugin e o item mudam de identidade a cada atualização da mesa; o que importa é isto
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, item.id, plugin.web?.sha256, reload, closed]);

  useEffect(() => {
    hostRef.current?.updateItem(item);
  }, [item]);

  const copyErrors = () => {
    const title = t(kind === 'sheet' ? 'webPlugin.errors.reportSheet' : 'webPlugin.errors.reportTable', { name: item.name, plugin: `${plugin.name} ${plugin.version}` });
    const text = [title, '', ...errors.map((e) => `${e.message}${e.stack ? `\n${e.stack}` : ''}`)].join('\n');
    void navigator.clipboard.writeText(text).then(() => toast(t('webPlugin.errors.copied'), 'success'));
  };

  if (!src) return <div className="rd-module__placeholder">{t('webPlugin.noEntry')}</div>;
  if (closed)
    return (
      <div className="rd-module__placeholder">
        <button type="button" className="rd-btn rd-btn--secondary" onClick={() => setClosed(false)}>
          {t('webPlugin.reopen')}
        </button>
      </div>
    );

  const restart = () => setReload((n) => n + 1);
  const menuEntries: MenuEntry[] = (menu?.items ?? []).map((m) =>
    'separator' in m
      ? { kind: 'separator' }
      : {
          label: m.label,
          ...(m.disabled ? { disabled: true } : {}),
          ...(m.danger ? { danger: true } : {}),
          onSelect: () => {
            menu?.resolve(m.id);
            setMenu(null);
          },
        },
  );

  return (
    <div className={`rd-webplugin${dev ? ' rd-webplugin--dev' : ''}`}>
      {dev ? (
        <div className="rd-webplugin__dev" title={dev}>
          {t('webPlugin.dev', { url: (() => {
            try {
              return new URL(dev).host;
            } catch {
              return dev;
            }
          })() })}
        </div>
      ) : null}
      <iframe
        key={`${src}#${reload}`}
        ref={ref}
        className="rd-webplugin__frame"
        title={item.name}
        sandbox="allow-scripts"
        allow=""
        referrerPolicy="no-referrer"
      />
      {state === 'loading' ? (
        <div className="rd-webplugin__overlay">
          <span className="rd-spinner" /> {t(kind === 'sheet' ? 'webPlugin.loading.sheet' : 'webPlugin.loading.table')}
        </div>
      ) : null}
      {state === 'timeout' || state === 'navigated' ? (
        <div className="rd-webplugin__overlay rd-webplugin__overlay--solid" role="alert">
          <p>{t(state === 'timeout' ? 'webPlugin.timeout' : 'webPlugin.navigated')}</p>
          <button type="button" className="rd-btn rd-btn--secondary" onClick={restart}>
            {t('webPlugin.reload')}
          </button>
        </div>
      ) : null}
      {state === 'hung' ? (
        <div className="rd-webplugin__bar" role="alert">
          <span>{t('webPlugin.hung')}</span>
          <button type="button" className="rd-btn rd-btn--secondary" onClick={restart}>
            {t('webPlugin.reload')}
          </button>
          <button type="button" className="rd-btn rd-btn--secondary" onClick={() => setClosed(true)}>
            {t('webPlugin.close')}
          </button>
        </div>
      ) : null}
      {errors.length ? (
        <button
          type="button"
          className="rd-webplugin__errbtn"
          aria-expanded={showErrors}
          aria-label={t('webPlugin.errors.button', { count: errors.length })}
          title={t('webPlugin.errors.title')}
          onClick={() => setShowErrors((v) => !v)}
        >
          ⚠ {errors.length}
        </button>
      ) : null}
      {showErrors && errors.length ? (
        <div className="rd-webplugin__errors" role="dialog" aria-label={t('webPlugin.errors.title')}>
          <div className="rd-webplugin__errhead">
            <strong>{t('webPlugin.errors.title')}</strong>
            <button type="button" className="rd-btn rd-btn--secondary" onClick={copyErrors}>
              {t('webPlugin.errors.copy')}
            </button>
            <button type="button" className="rd-btn rd-btn--secondary" onClick={() => setErrors([])}>
              {t('webPlugin.errors.clear')}
            </button>
          </div>
          <ul>
            {errors
              .slice()
              .reverse()
              .map((e, i) => (
                <li key={`${e.at}-${i}`}>{e.message}</li>
              ))}
          </ul>
        </div>
      ) : null}
      {menu ? (
        <ContextMenu
          anchor={menu.at}
          entries={menuEntries}
          label={plugin.name}
          onClose={() => {
            // o ContextMenu fecha antes de chamar o onSelect do item clicado: o "nada" espera um
            // instante, e a escolha, que vem logo em seguida, chega primeiro
            const aberto = menu;
            setMenu(null);
            queueMicrotask(() => aberto.resolve(null));
          }}
        />
      ) : null}
      {picker ? (
        <ImagePickerDialog
          allowWeb={false}
          onCancel={() => {
            picker.resolve(null);
            setPicker(null);
          }}
          onPick={(value) => {
            picker.resolve(rpgDeskFileId(value));
            setPicker(null);
          }}
        />
      ) : null}
    </div>
  );
}
